import React, { useCallback, useEffect, useState } from "react";
import { GripVertical, LoaderCircle, Pencil, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { DeleteConfirmationButton } from "../common/ConfirmationDialog";
import ListPagination from "../common/ListPagination";
import apiClient from "../../lib/utils/network-client";
import useDragAutoScroll from "../../hooks/useDragAutoScroll";
import useToast from "../../hooks/useToast";
import { FIELD_CONFIG, MSR_DATASETS } from "./msrConfig";
import MsrRecordDialog from "./MsrRecordDialog";

const MsrRecordsPanel = ({ dataset, canCreate, canEdit, canDelete }) => {
  const config = MSR_DATASETS[dataset];
  const { showErrorToast, showSuccessToast } = useToast();
  const [records, setRecords] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [department, setDepartment] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [draggedId, setDraggedId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const hasReorderConstraints = Boolean(appliedSearch) || department !== "all";
  const canReorder = canEdit && !hasReorderConstraints && !isLoading && !isReordering && records.length > 1;
  const { handleDragAutoScroll, stopDragAutoScroll } = useDragAutoScroll(
    Boolean(draggedId),
    canReorder,
  );

  const fetchRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get(`/msr/admin/records/${dataset}`, {
        params: {
          page,
          limit: pageSize,
          search: appliedSearch || undefined,
          department: department === "all" ? undefined : department,
        },
      });
      const data = response.data?.data || {};
      setRecords(data.records || []);
      setDepartments(data.departments || []);
      setTotal(Number(data.pagination?.total || 0));
    } catch (err) {
      showErrorToast(err.response?.data?.message || `Failed to load ${config.label.toLowerCase()}`);
    } finally {
      setIsLoading(false);
    }
  }, [appliedSearch, config.label, dataset, department, page, pageSize, showErrorToast]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const openCreate = () => {
    setEditingRecord(null);
    setDialogOpen(true);
  };

  const openEdit = (record) => {
    setEditingRecord(record);
    setDialogOpen(true);
  };

  const handleDelete = async (record) => {
    try {
      await apiClient.delete(`/msr/records/${dataset}/${record.id}`);
      showSuccessToast(`${config.label} record deleted successfully`);
      fetchRecords();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to delete MSR record");
    }
  };

  const applySearch = (event) => {
    event.preventDefault();
    setPage(1);
    setAppliedSearch(search.trim());
  };

  const handleDrop = async (event, targetId) => {
    event.preventDefault();
    stopDragAutoScroll();
    if (!canReorder || !draggedId || draggedId === targetId) {
      setDraggedId(null);
      setDropTargetId(null);
      return;
    }

    const sourceIndex = records.findIndex((item) => item.id === draggedId);
    const targetIndex = records.findIndex((item) => item.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) {
      setDraggedId(null);
      setDropTargetId(null);
      return;
    }

    const previousRecords = [...records];
    const reorderedRecords = [...records];
    const [movedRecord] = reorderedRecords.splice(sourceIndex, 1);
    reorderedRecords.splice(targetIndex, 0, movedRecord);
    const pageOffset = (page - 1) * pageSize;
    const normalizedRecords = reorderedRecords.map((item, index) => ({
      ...item,
      sort_order: pageOffset + index + 1,
    }));

    setRecords(normalizedRecords);
    setDraggedId(null);
    setDropTargetId(null);
    setIsReordering(true);
    try {
      await apiClient.put(`/msr/records/${dataset}/reorder`, {
        orderedItems: normalizedRecords.map((item) => ({
          id: item.id,
          sort_order: item.sort_order,
        })),
      });
      showSuccessToast(`${config.label} order updated successfully`);
    } catch (err) {
      setRecords(previousRecords);
      showErrorToast(err.response?.data?.message || "Unable to update MSR record order");
    } finally {
      setIsReordering(false);
    }
  };

  return (
    <div className="space-y-4" onDragOver={handleDragAutoScroll}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <form onSubmit={applySearch} className="flex min-w-0 flex-1 gap-2 lg:max-w-xl">
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={`Search ${config.label.toLowerCase()}...`}
              className="rounded-lg pl-9"
            />
          </div>
          <Button type="submit" variant="outline">Search</Button>
        </form>

        <div className="flex flex-wrap gap-2">
          {departments.length > 0 && (
            <Select value={department} onValueChange={(value) => { setDepartment(value); setPage(1); }}>
              <SelectTrigger className="w-56"><SelectValue placeholder="All departments" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                {departments.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button type="button" variant="outline" onClick={fetchRecords}>
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
          {canCreate && (
            <Button type="button" onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" /> Add Record
            </Button>
          )}
        </div>
      </div>

      {canEdit && (
        <p className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] ${hasReorderConstraints ? "text-amber-600" : "text-slate-400"}`}>
          {isReordering ? <LoaderCircle className="h-4 w-4 animate-spin text-primary" /> : <GripVertical className="h-4 w-4" />}
          {isReordering
            ? "Saving order..."
            : hasReorderConstraints
              ? "Clear search and department filters to reorder"
              : "Drag a row handle to reorder; hold near an edge to auto-scroll"}
        </p>
      )}

      <div className="overflow-hidden rounded-lg border border-white/60 bg-white/80 shadow-sm backdrop-blur-xl">
        {isLoading ? (
          <div className="py-16 text-center text-sm font-medium text-slate-500">Loading records...</div>
        ) : records.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-500">No matching records found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[900px] w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="w-20 px-4 py-4 text-left text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Reorder</th>
                  {config.columns.map((field) => (
                    <th key={field} className="px-4 py-4 text-left text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
                      {FIELD_CONFIG[field].label}
                    </th>
                  ))}
                  <th className="px-4 py-4 text-left text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Status</th>
                  <th className="px-4 py-4 text-right text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {records.map((record) => (
                  <tr
                    key={record.id}
                    onDragOver={(event) => {
                      if (!canReorder || !draggedId) return;
                      event.preventDefault();
                      event.dataTransfer.dropEffect = "move";
                      if (draggedId !== record.id) setDropTargetId(record.id);
                    }}
                    onDrop={(event) => handleDrop(event, record.id)}
                    className={`align-top transition ${
                      draggedId === record.id
                        ? "bg-blue-50/70 opacity-50"
                        : dropTargetId === record.id
                          ? "bg-primary/5 ring-2 ring-inset ring-primary/20"
                          : "hover:bg-slate-50/70"
                    }`}
                  >
                    <td className="px-4 py-4">
                      <button
                        type="button"
                        draggable={canReorder}
                        disabled={!canReorder}
                        aria-label={`Drag to reorder ${record.name || config.singular}`}
                        title={hasReorderConstraints ? "Clear search and filters to reorder" : "Drag to reorder"}
                        onDragStart={(event) => {
                          if (!canReorder) return;
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData("text/plain", record.id);
                          setDraggedId(record.id);
                        }}
                        onDragEnd={() => {
                          stopDragAutoScroll();
                          setDraggedId(null);
                          setDropTargetId(null);
                        }}
                        className="inline-flex h-9 w-9 cursor-grab items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-primary/40 hover:bg-primary/5 hover:text-primary active:cursor-grabbing disabled:cursor-not-allowed disabled:border-slate-100 disabled:bg-slate-50 disabled:text-slate-300"
                      >
                        <GripVertical className="h-4 w-4" />
                      </button>
                    </td>
                    {config.columns.map((field) => (
                      <td key={field} className={`max-w-sm whitespace-pre-line px-4 py-4 text-sm text-slate-700 ${field === "name" ? "font-bold text-slate-900" : ""}`}>
                        {record[field] ?? "—"}
                      </td>
                    ))}
                    <td className="px-4 py-4">
                      <Badge className={record.status === "published" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}>
                        {record.status === "published" ? "Published" : "Draft"}
                      </Badge>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex justify-end gap-2">
                        {canEdit && (
                          <Button type="button" variant="outline" className="rounded-lg" onClick={() => openEdit(record)}>
                            <Pencil className="h-4 w-4" />
                            <span className="sr-only">Edit {record.name}</span>
                          </Button>
                        )}
                        {canDelete && (
                          <DeleteConfirmationButton
                            title="Delete MSR record?"
                            description={`This will permanently delete “${record.name}” from ${config.label}.`}
                            onConfirm={() => handleDelete(record)}
                          >
                            <Button type="button" variant="outline" className="rounded-lg border-red-200 text-red-600 hover:bg-red-50">
                              <Trash2 className="h-4 w-4" />
                              <span className="sr-only">Delete {record.name}</span>
                            </Button>
                          </DeleteConfirmationButton>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <ListPagination
          page={page}
          pageSize={pageSize}
          totalItems={total}
          onPageChange={setPage}
          onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          itemLabel="records"
          pageSizeOptions={[10, 25, 50, 100]}
        />
      </div>

      <MsrRecordDialog
        dataset={dataset}
        record={editingRecord}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={fetchRecords}
      />
    </div>
  );
};

export default MsrRecordsPanel;
